/**
 * How a returning guest says "one of the seats you are keeping is mine" without saying which.
 *
 * The seat hold is what makes a reconnect the same player, and the four-player cap is what makes it
 * worth holding: a held seat counts as taken, so nobody sits in the chair somebody is walking back
 * to. The two rules meet badly at the door. A guest dialling back into a session that was full when
 * it dropped reads zero free seats out of the host's hello and turns itself away before it can send
 * the `rejoin` frame that would have named the seat as its own.
 *
 * So the claim moves into the handshake, where the count is made. The token itself cannot go there:
 * a Trystero room is a mesh, and every guest exchanges a hello with every other guest before the
 * role refusal tears the pair down, so a token in a hello would reach exactly the people it exists
 * to keep out of that seat. What goes instead is a digest of the nonce the hello already carries
 * and the token, which the host can check against every seat it is keeping and nobody else can
 * turn back into a token.
 *
 * It proves possession, not identity, and it is only ever worth a chair at the door. The seat still
 * has to be reclaimed by presenting the real token on the reliable channel, where the host is the
 * only one listening, so a replayed digest buys an imposter a connection and no seat.
 *
 * Eight bytes of a SHA-256, because a guess has one fifteen-second hold to land in.
 */

/** Half a SHA-256 would be sixteen. Eight is already far past what a hold's lifetime allows. */
export const SEAT_PROOF_BYTES = 8;

/** What a side with nothing to claim sends: a host, and a guest that has never sat here. */
export const NO_SEAT_PROOF: Uint8Array = new Uint8Array(SEAT_PROOF_BYTES);

/**
 * Salted, so a digest is only ever meaningful in the exchange it was made for. Without it the same
 * bytes would mean something anywhere else a nonce and a token are hashed together.
 */
const PROOF_LABEL = 'tbd-seat-claim';

/** The claim a guest puts in its hello, over the nonce it is about to send with it. */
export async function proveSeat(nonce: number, token: string): Promise<Uint8Array> {
  const source = new TextEncoder().encode(`${PROOF_LABEL}:${String(nonce >>> 0)}:${token}`);
  const digest = await crypto.subtle.digest('SHA-256', source);
  return new Uint8Array(digest).slice(0, SEAT_PROOF_BYTES);
}

/**
 * Whether a claim is one of these seats, checked by the only side that holds the tokens.
 *
 * An empty claim is not a claim. A guest that has nothing to present sends zeroes, and a host that
 * is keeping nothing has nothing to check them against, so both answer here rather than hashing.
 */
export async function claimsHeldSeat(
  nonce: number,
  proof: Uint8Array,
  tokens: readonly string[],
): Promise<boolean> {
  if (tokens.length === 0 || isEmptyProof(proof)) return false;
  for (const token of tokens) {
    if (sameBytes(await proveSeat(nonce, token), proof)) return true;
  }
  return false;
}

/** Zeroes mean nothing is being claimed, which every side that claims nothing sends. */
export function isEmptyProof(proof: Uint8Array): boolean {
  return proof.every((byte) => byte === 0);
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  return a.length === b.length && a.every((byte, index) => byte === b[index]);
}
